import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import GenericTable from "@components/tables/GenericTable";
import { useNavigate } from "react-router-dom";
import { setFileStatus, setSelectedAssignedFileId, setTargetLanguageId, setTotalSentences } from "@store/slices/devnagriValidatorSlice";
import { fetchInProgressFiles, fetchSentencesByFileId } from "@store/thunks/devnagriValidationAdminThunk";
import { getStatusStyle, restrictSpecialCharacters } from "@utils/constants";
import { setFileMeta } from "@/store/slices/fileMetaSlice";
import { setFileAndLanguage } from "@/store/slices/fileLanguageSlice";

const FileDetailTableDevnagriValidator = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { selectedAssignedFolderId, assignedFiles } = useSelector((store) => store.devnagriValidator);
  const files = selectedAssignedFolderId ? (assignedFiles[selectedAssignedFolderId] || []) : [];
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [popup, setPopup] = useState({ isOpen: false, type: "", message: "" });

  // Handle input change and key press
  const handleInputChange = (e) => {
   const newValue = e.target.value;
    if (restrictSpecialCharacters(newValue) || newValue === "") {
      setInputValue(newValue);
      setSearchQuery(newValue);
      setPageIndex(0);
      setHasNextPage(true); // Reset search → assume more pages exist
    } else {
      setPopup({
        isOpen: true,
        type: "error",
        message: "Special characters are not allowed. Use only letters, numbers, and spaces.",
      });
    }

  };

  const handlePopupClose = () => {
    setPopup({ isOpen: false, type: "", message: "" });
  };

  const handleKeyDown = (e) => {
    setSearchQuery(e.target.value);
  };

  useEffect(() => {
    const fetchFolders = async () => {
      setInitialLoading(true);
      setLoading(true);
      setError(null);
      try {
        const response = await dispatch(fetchInProgressFiles(selectedAssignedFolderId, searchQuery, pageIndex, pageSize));
        console.log(response)
        if (Array.isArray(response)) {
          if (response.length < pageSize) {
            setHasNextPage(false);
          } else {
            setHasNextPage(true);
          }
        }
        setInitialLoading(false);
      } catch (err) {
        setError(err.message);
        setInitialLoading(false);
      } finally {
        setLoading(false);
      }
    };
    fetchFolders();
  }, [dispatch,selectedAssignedFolderId,pageIndex,pageSize,searchQuery]);

  // Filter folders based on search query
  const filteredFiles = useMemo(() => {
    if (!searchQuery) return files
    return files.filter(
      (project) =>
        project.fileName?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [files, searchQuery]);

  const handleFileClick = (fileId, row) => {
    console.log("File clicked:", fileId, row);
    const file = row.original;
    dispatch(setTotalSentences(row.original.translatedSentenceCount))
    dispatch(setSelectedAssignedFileId(fileId));
    dispatch(setTargetLanguageId(row.original.targetId));
    dispatch(setFileStatus(row.original.fStatus || "Pending"));
    dispatch(setFileMeta({
      fileName: row.original.fileName,
    }))
    dispatch(setFileAndLanguage({
      targetLanguageName: row.original.targetName,
    }))

    // Fetch sentences for this file using the new API fields
    dispatch(fetchSentencesByFileId(
      file.fileId,
      file.validatorUserId,
      file.sourceId,
      file.targetId
    ));

    navigate("/devnagriValidator/folders/files/sentences", {
      state: { requiredLanguage: file.targetName }
    });
  };


  const fileColumns = [
    {
      header: "Sr. No.",
      cell: ({ row, table }) => {
        const rowIndex = row.index;

        return pageIndex * pageSize + rowIndex + 1; // Serial number starts from 1
      },
    },
    {
      header: "File Name",
      accessorKey: "fileName",
      cell: ({ row }) => (
        <span
          className="cursor-pointer hover:underline text-[#ec7026] hover:text-[#d85d1a] font-['Semantic/Regular']"
          onClick={() => handleFileClick(row.original.fileId, row)}
        >
          {row.original.fileName}
        </span>
      ),
    },
    {
      header: "Translated Sentences",
      accessorKey: "translatedSentenceCount",
      cell: ({ row }) => (
        <span>{row.original.translatedSentenceCount}</span>
      ),
    },
    {
      header: "Validated Sentences",
      accessorKey: "validatedSentenceCount",
      cell: ({ row }) => (
        <span>{row.original.validatedSentenceCount}</span>
      ),
    },
    {
      header: "Rejected Sentences",
      accessorKey: "rejectedSentenceCount",
      cell: ({ row }) => (
        <span>{row.original.rejectedSentenceCount}</span>
      ),
    },
    {
      header: "Progress",
      accessorKey: "percentageCompleted",
      cell: ({ row }) => {
        const percentage = row.original.percentageCompleted || 0;
        return (
          <div className="flex items-center">
            <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
              <div
                className="bg-blue-600 h-2 rounded-full"
                style={{ width: `${percentage}%` }}
              ></div>
            </div>
            <span className="text-sm text-gray-600">{percentage}%</span>
          </div>
        );
      },
    },
    {
      header: "Status",
      accessorKey: "fStatus",
      cell: ({ row }) => {
        console.log(row)
        const style = getStatusStyle(row.original.fStatus || row.fStatus);
        return (
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}
          >
            {style.label}
          </span>
        );
      },
    },
    {
      header: "Source Language",
      accessorKey: "sourceName",
      cell: ({ row }) => (
        <span>{row.original.sourceName || "-"}</span>
      ),
    },
    {
      header: "Target Language",
      accessorKey: "targetName",
      cell: ({ row }) => (
        <span>{row.original.targetName || "-"}</span>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="p-6 mt-6">
        <h2 className="text-xl font-bold mb-4">Loading projects...</h2>
        <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
        <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
        <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 mt-6">
        <h2 className="text-xl font-bold mb-4">Error Loading Projects</h2>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>{error}</p>
          <p className="mt-2">Please try again later or contact support.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] bg-gray-50/50">
      <div className="lg:pr-8 lg:pt-8 flex justify-end items-center">
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            placeholder="Search Files..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] text-base font-['Semantic/Strong'] text-gray-700 pl-10"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {popup.isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
              {popup.type === "error" ? "Error" : "Info"}
            </h2>
            <p className="mt-2 text-sm text-gray-500 font-['Semantic/Strong']">
              {popup.message}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handlePopupClose}
                className="inline-flex items-center px-4 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      
      <div className="w-full p-4 sm:p-6 lg:p-8">
        {initialLoading || loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
            <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
            <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg font-['Semantic/Strong']">
              <p>{error}</p>
              <p className="mt-2 text-sm">Please try again later or contact support.</p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="w-full overflow-x-auto">
              {filteredFiles.length === 0 ? (
                <div className="p-6">
                  <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded font-['Semantic/Strong']">
                    <p>No projects assigned yet.</p>
                    <p className="mt-2">
                      You have not been assigned any projects for validation. Please contact the administrator if you believe this is an error.
                    </p>
                  </div>
                </div>
              ) : (
                <GenericTable
                  data={[...filteredFiles].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))}
                  columns={fileColumns} showPagination={true}
                  manualPagination={true}
                  pageIndex={pageIndex}  
                  pageSize={pageSize} 
                  hasNextPage={hasNextPage} 
                  onPaginationChange={({ pageIndex, pageSize }) => {
                    setPageIndex(pageIndex);
                    setPageSize(pageSize);
                  }} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileDetailTableDevnagriValidator;
